import { toast } from "react-toastify";
import { push } from "connected-react-router";
import { InferType, array, mixed, object, string } from "yup";
import { AppDispatch, RootState } from "../../../config/store";
import { ROUTES } from "../../../config/routes";
import { resetLogin, setUser } from "./AdminLoginSlice";
import adminAxios from "../../../config/adminAxios";

export const ROLES = ["ADMIN", "MEMBER"] as const;
export type typeRoles = (typeof ROLES)[number];

const userSchema = object({
  uuid: string().required(),
  logo: string().nullable().defined(),
  name: string().required(),
  email: string().required(),
  userOrganisations: array(
    object({
      role: mixed<typeRoles>().oneOf(ROLES).required(),
      organisation: object({
        uuid: string().required(),
        name: string().required(),
      }),
    })
  ).required(),
}).required();

const getUserResponseSchema = object({
  message: string().required(),
  user: userSchema,
}).required();

type typeGetUserResponseSchema = InferType<typeof getUserResponseSchema>;
export type typeUser = typeGetUserResponseSchema["user"];

export const getUser =
  (callback?: () => void) =>
  (dispatch: AppDispatch, getState: () => RootState) =>
    adminAxios({ user: getState().admin.login.user })
      .get<typeGetUserResponseSchema>("me")
      .then(async (response) => {
        const validResponse = await getUserResponseSchema.validate(
          response.data
        );

        if (callback) {
          dispatch(setUser(validResponse.user));
          callback();
        }
      })
      .catch((error) => {
        toast.error(error.response?.data?.message ?? error.message);
        console.error(error);
      });

export const loginRequestSchema = object({
  usernameEmail: string().required().min(1),
  password: string().required().min(1),
});

type typeLoginRequestSchema = InferType<typeof loginRequestSchema>;

const loginResponseSchema = object({
  data: object({
    message: string().required(),
  }),
});

export const login =
  (form: typeLoginRequestSchema) =>
  (dispatch: AppDispatch, getState: () => RootState) =>
    adminAxios({ user: getState().admin.login.user })
      .post<typeLoginRequestSchema, InferType<typeof loginResponseSchema>>(
        "authentication/login",
        form
      )
      .then(async (response) => {
        const validResponse = await loginResponseSchema.validate(response);
        dispatch(getUser(() => dispatch(push(ROUTES.adminSettings.path))));
        toast.success("Successfully logged in");
      })
      .catch((error) =>
        toast.error(error.response?.data?.message ?? error.message ?? error)
      )
      .finally(() => dispatch(resetLogin()));

export const checkSession =
  () => (dispatch: AppDispatch, getState: () => RootState) =>
    adminAxios({ user: getState().admin.login.user })
      .get("authentication/session")
      .then(() => {
        dispatch(getUser(() => dispatch(push(ROUTES.adminSettings.path))));
      })
      .catch(() => null)
      .finally(() => dispatch(resetLogin()));
